/**
 * TypeScript types for Purchase Order Lines (Bestillingslinje) feature
 * Maps to backend POrderLineWithHeaderDto and PagedResult<T>
 */

/**
 * Generic paginated result wrapper
 * Maps to BizClick.WMS.Shared.DTOs.PagedResult<T>
 */
export interface PagedResult<T> {
  /** The items in the current page */
  items: T[];

  /** Total number of records across all pages */
  totalRecords: number;

  /** Current page number (1-based) */
  pageNumber: number;

  /** Number of items per page */
  pageSize: number;

  /** Total number of pages */
  totalPages: number;

  /** Whether there is a previous page */
  hasPreviousPage: boolean;

  /** Whether there is a next page */
  hasNextPage: boolean;
}

/**
 * Purchase order line with header information
 * Maps to BizClick.WMS.Shared.DTOs.POrderLineWithHeaderDto
 * Used for displaying order lines with parent order details
 */
export interface POrderLineWithHeaderDto {
  // Line information
  id: number;
  companyNo: string;
  orderNo: number;
  lineNo: number;
  lineNoERP?: number;
  lineStatus: number;
  lineStatusText: string;
  itemNo: string;
  itemBarCode?: string;
  description1?: string;
  itemGroup1?: string;
  itemGroup2?: string;
  itemGroup3?: string;
  quantity?: number;
  quantityDelivered?: number;
  remainingQuantity: number;
  itemUnitCode?: string;
  itemUnitTxt?: string;
  price?: number;
  discount?: number;
  deliveryDate?: string;
  serialNoItemYesNo?: boolean;
  stdLocationName?: string;
  warehouse?: string;
  itemBatchNo?: string;
  batchItemYesNo?: boolean;
  orderLineFreeField1?: string;
  orderLineFreeField2?: string;
  orderLineFreeField3?: string;
  lineCreated?: string;
  lineLastUpdate?: string;
  lineCreatedBy?: string;
  lineUpdatedBy?: string;

  // Header information
  orderNoERP: string;
  orderStatus: number;
  orderStatusText: string;
  orderType?: string;
  orderDate?: string;
  orderDeliveryDate?: string;
  supplierNo: string;
  supplierName?: string;
  supplierOrderNo?: string;
  yourRef?: string;
  ourRef?: string;
  deliveryMethod?: string;
  carrierName?: string;
  contactName?: string;
  orderCreated?: string;
  orderLastUpdate?: string;
  orderCreatedBy?: string;
  orderUpdatedBy?: string;
}

/**
 * Query parameters for fetching purchase order lines
 * Used for pagination, filtering, and sorting
 */
export interface POrderLinesQueryParams {
  /** Page number (1-based, default: 1) */
  pageNumber?: number;

  /** Records per page (default: 50, max: 500) */
  pageSize?: number;

  /** Column to sort by (default: OrderNo) */
  sortColumn?: string;

  /** Sort direction: 'asc' or 'desc' (default: desc) */
  sortDirection?: 'asc' | 'desc';

  /** Filter by item number (partial match) */
  itemNo?: string;

  /** Filter by supplier number (partial match) */
  supplierNo?: string;

  /** Filter by order date from (inclusive) */
  fromDate?: Date | string;

  /** Filter by order date to (inclusive) */
  toDate?: Date | string;

  /** Filter by maximum order status (inclusive) */
  maxOrderStatus?: number;
}

/**
 * Purchase order status enum
 * Matches the status values used in the backend
 */
export enum POrderStatus {
  Ubehandlet = 0,
  Godkjent = 10,
  UnderArbeid = 20,
  BehandletIkkeKomplett = 30,
  BehandletKomplett = 40,
  TilPakking = 45,
  KlarForEksport = 50,
  Eksportert = 60
}

/**
 * Helper function to get status text for a given status code
 */
export function getOrderStatusText(status: number): string {
  switch (status) {
    case POrderStatus.Ubehandlet:
      return 'Ubehandlet';
    case POrderStatus.Godkjent:
      return 'Godkjent';
    case POrderStatus.UnderArbeid:
      return 'Under arbeid';
    case POrderStatus.BehandletIkkeKomplett:
      return 'Behandlet, ikke komplett';
    case POrderStatus.BehandletKomplett:
      return 'Behandlet, komplett';
    case POrderStatus.TilPakking:
      return 'Til pakking';
    case POrderStatus.KlarForEksport:
      return 'Klar for eksport';
    case POrderStatus.Eksportert:
      return 'Eksportert';
    default:
      return `Status ${status}`;
  }
}
